import { Suspense } from "react";

import { LeftSideMenu } from "@impero-com/common/components/leftSideMenu";
import { useTranslation } from "@impero-com/common/utils/i18n";
import {
    ActionMenu,
    ActionMenuProps,
    ApplicationBarToolbar,
    PageColumns,
    PageContent,
    PageLayout,
    PageNavMenu,
    PageNavMenuItem,
    PageNavMenuSection,
    PageSpinner,
} from "@impero-com/ui";
import { useParams } from "react-router-dom";

import { OrgApplicationBarHeader } from "components/applicationBar/OrgApplicationBarHeader";
import { PageNavMenuLayout, PageNavMenuMain } from "components/layout";
import { useEntityCountQuery } from "org/entities/queries/useEntityCountQuery";
import { useEntityQuery } from "org/entities/queries/useEntityQuery";
import { useNewMenu } from "org/navigation/useNewMenu";
import { WebRoute } from "routes";

import { buildEntityProgramsListUrl } from "../urlUtils";
import { useEntitiesBreadCrumbs } from "../useEntitiesBreadCrumbs";

export const NAVIGATION_ID_ENTITY_DETAILS = "ENTITY_DETAILS";
export const NAVIGATION_ID_CONTROL_PROGRAMS = "CONTROL_PROGRAMS";
const NAVIGATION_ID_ACCESS = "ACCESS";

interface EntityViewSideMenuProps {
    navigationId: string;
    actions?: ActionMenuProps;
    children: React.ReactNode;
}

export function EntityViewSideMenu({
    navigationId,
    actions,
    children,
}: EntityViewSideMenuProps) {
    const { t } = useTranslation();
    const { id } = useParams();
    const entityId = Number(id);
    const { data } = useEntityQuery(entityId);
    const {
        data: { linkedControlPrograms: count },
    } = useEntityCountQuery(entityId);
    const newMenu = useNewMenu();
    const breadCrumbs = useEntitiesBreadCrumbs();

    const controlEntity = data.content.resource;
    const title = {
        title: controlEntity.name,
        breadCrumbs,
    };
    const actionMenu = actions && (
        <ActionMenu id={actions.id} actions={actions.actions} />
    );

    if (newMenu) {
        return (
            <PageNavMenuLayout
                title={title}
                menu={
                    <EntityViewMenu
                        entityId={entityId}
                        entityName={controlEntity.name}
                        navigationId={navigationId}
                        controlProgramCount={count}
                    />
                }
            >
                <PageNavMenuMain actions={actionMenu}>
                    <Suspense fallback={<PageSpinner />}>{children}</Suspense>
                </PageNavMenuMain>
            </PageNavMenuLayout>
        );
    }

    return (
        <PageLayout fullHeight>
            <OrgApplicationBarHeader title={title}>
                <ApplicationBarToolbar actions={actionMenu} />
            </OrgApplicationBarHeader>
            <PageColumns>
                <LeftSideMenu
                    selectedItemId={navigationId}
                    onItemSelected={() => {}}
                    items={[
                        {
                            type: "SimpleMenuItem",
                            id: NAVIGATION_ID_ENTITY_DETAILS,
                            label: t("Entity_Navigation_Item_Label"),
                            handler: {
                                type: "ROUTER",
                                linkTo: `${WebRoute.ENTITIES}/${entityId}`,
                            },
                        },
                        {
                            type: "SubMenuItem",
                            id: NAVIGATION_ID_ACCESS,
                            label: t("Entity_Navigation_Item_Label_Access"),
                            childItems: [
                                {
                                    type: "SimpleMenuItem",
                                    id: NAVIGATION_ID_CONTROL_PROGRAMS,
                                    label: t(
                                        "Entity_Navigation_Item_Label_Programs",
                                        {
                                            count,
                                        }
                                    ),
                                    handler: {
                                        type: "ROUTER",
                                        linkTo: buildEntityProgramsListUrl(
                                            entityId
                                        ),
                                    },
                                },
                            ],
                        },
                    ]}
                />
                <PageContent>{children}</PageContent>
            </PageColumns>
        </PageLayout>
    );
}

interface EntityViewMenuProps {
    entityId: number;
    entityName: string;
    navigationId: string;
    controlProgramCount: number;
}

function EntityViewMenu({
    entityId,
    entityName,
    navigationId,
    controlProgramCount,
}: EntityViewMenuProps) {
    const { t } = useTranslation();

    return (
        <PageNavMenu aria-label={entityName}>
            <PageNavMenuSection>
                <PageNavMenuItem
                    label={t("Entity_Navigation_Item_Label")}
                    to={`${WebRoute.ENTITIES}/${entityId}`}
                    active={navigationId === NAVIGATION_ID_ENTITY_DETAILS}
                />
            </PageNavMenuSection>
            <PageNavMenuSection>
                <PageNavMenuItem
                    label={t("ControlPrograms")}
                    to={buildEntityProgramsListUrl(entityId)}
                    count={
                        controlProgramCount === 0
                            ? undefined
                            : controlProgramCount
                    }
                    active={navigationId === NAVIGATION_ID_CONTROL_PROGRAMS}
                />
            </PageNavMenuSection>
        </PageNavMenu>
    );
}
